import React, { useEffect, useState } from 'react';
import { previewAddBranch, addBranch, isPaymentsConfigured, BranchQuote } from '../services/paymentsApi';
import { customToast } from '../utils/customToast';
import { V, PrimaryButton, GhostButton } from './SubscriptionUI';

/* ============================================================================
   AddBranchPanel — the Enterprise "add another branch" flow, shown inside the
   Manage-Subscription modal.

   A branch is NOT a line item: it's a whole organization of its own — its own
   buses, students, drivers and logins — created the same way the Trasse console
   creates an org, then linked to this one by enterpriseGroupId. Admins of the
   group can switch between branches from a single login; the data stays fully
   isolated per org.

   Billing, as agreed:
     • each branch is billed on ITS OWN size at the Enterprise rate;
     • the whole group shares ONE renewal date — the mother org's — so adding a
       branch mid-cycle charges only the prorated remainder to align it;
     • the mother's card pays by default, and a branch can override later.
   ========================================================================== */

export default function AddBranchPanel({
  motherOrgCode, motherName, unit, renewsOn, open, onOpen, onCancel, onCreated,
  registerCreate,
}: {
  motherOrgCode: string;
  motherName: string;
  unit: 'child' | 'vehicle';
  renewsOn: Date;
  open: boolean;
  onOpen: () => void;
  onCancel: () => void;
  /** Called with the new branch org code on success so the parent can switch into it. */
  onCreated: (branchOrgCode: string) => void;
  /** Lets the modal footer run the same create action as the in-form button. */
  registerCreate?: (run: (() => void) | null) => void;
}) {
  const [name, setName] = useState('');
  const [size, setSize] = useState<number>(0);
  const [quote, setQuote] = useState<BranchQuote | null>(null);
  const [pricing, setPricing] = useState(false);
  const [creating, setCreating] = useState(false);

  const unitWord = unit === 'child' ? 'children' : 'vehicles';

  // Re-price as the size settles. Debounced so typing "120" doesn't fire three
  // quotes, and skipped entirely when the size is meaningless.
  useEffect(() => {
    if (!open || size <= 0) { setQuote(null); return; }
    let cancelled = false;
    setPricing(true);
    const t = window.setTimeout(async () => {
      const q = await previewAddBranch(motherOrgCode, size);
      if (!cancelled) { setQuote(q); setPricing(false); }
    }, 450);
    return () => { cancelled = true; window.clearTimeout(t); setPricing(false); window.clearTimeout(t); };
  }, [open, size, motherOrgCode]);

  async function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) { customToast.show('Give the branch a name.', 'error'); return; }
    if (size <= 0) { customToast.show(`Enter how many ${unitWord} this branch has.`, 'error'); return; }

    // Creating an organization (and charging a card) is not undoable from the
    // UI, so confirm explicitly with the real figure rather than a placeholder.
    const billingLine = quote?.manual
      ? 'This account is billed manually, so nothing is charged here — the branch appears on your next invoice.'
      : `You'll be charged Ksh ${(quote?.amountKES ?? 0).toLocaleString()} now to align it to your renewal on `
        + `${renewsOn.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}, `
        + `then Ksh ${(quote?.recurringKES ?? 0).toLocaleString()} per cycle after that.`;
    const ok = window.confirm(
      `Add "${trimmed}" as a branch of ${motherName}?\n\n${billingLine}\n\n`
      + 'The branch gets its own buses, students and logins.',
    );
    if (!ok) return;

    setCreating(true);
    try {
      const res = await addBranch(motherOrgCode, { name: trimmed, size });
      if (res.ok) {
        const code = res.orgCode || '';
        customToast.show(
          code
            ? `"${trimmed}" is live. Opening it now…`
            : `"${trimmed}" is live. Use the Branch menu in the header to open it.`,
          'success',
        );
        onCreated(code);
      } else if (res.fallback === 'hosted' && res.authorization_url) {
        window.location.href = res.authorization_url;
      } else {
        customToast.show(res.message || 'Could not create the branch.', 'error');
      }
    } catch (e) {
      customToast.show((e as Error)?.message || 'Could not create the branch.', 'error');
    } finally {
      setCreating(false);
    }
  }

  // Keep the footer wired to THIS create — otherwise "+ Add a branch" only
  // re-opens the form and looks broken once the fields are already filled.
  useEffect(() => {
    if (!registerCreate) return;
    registerCreate(open ? () => { void handleCreate(); } : null);
    return () => registerCreate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recreate when inputs that affect create change
  }, [registerCreate, open, name, size, quote, motherOrgCode, motherName, renewsOn, creating]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={onOpen}
        style={{
          width: '100%', font: 'inherit', cursor: 'pointer', textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px',
          border: `1.5px dashed ${V.line}`, borderRadius: 14, background: '#fff', color: V.ink,
        }}
      >
        <span style={{
          width: 34, height: 34, borderRadius: 10, background: '#f3f1fd', color: V.violet,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, fontWeight: 800,
        }}>+</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 14.5, fontWeight: 700 }}>Add a branch</span>
          <span style={{ fontSize: 12.5, color: V.faint }}>
            A separate school or depot under {motherName} — own buses, own logins, one bill.
          </span>
        </div>
      </button>
    );
  }

  return (
    <div style={{ border: `1.5px solid ${V.violet}`, borderRadius: 14, padding: '18px 20px', background: 'rgba(91,71,214,.03)' }}>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: V.ink, marginBottom: 4 }}>New branch</div>
      <p style={{ margin: '0 0 16px', fontSize: 12.5, color: V.muted, lineHeight: 1.55 }}>
        This creates a full organization of its own, linked to {motherName}. Its students, drivers and
        buses stay completely separate — you'll be able to switch between branches from one login.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Field label="Branch name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Mombasa Campus"
            style={inputStyle}
          />
        </Field>
        <Field label={`How many ${unitWord}?`}>
          <input
            type="number"
            min={1}
            value={size || ''}
            onChange={(e) => setSize(Math.max(0, parseInt(e.target.value, 10) || 0))}
            placeholder="0"
            style={inputStyle}
          />
        </Field>
      </div>

      {/* Live price for THIS branch. */}
      <div style={{
        marginTop: 14, background: '#fff', border: `1px solid ${V.hair}`,
        borderRadius: 12, padding: '14px 16px', fontSize: 13, color: V.body,
      }}>
        {!isPaymentsConfigured() ? (
          <span style={{ color: V.faint }}>Payments backend not configured — the branch will be created without a charge.</span>
        ) : size <= 0 ? (
          <span style={{ color: V.faint }}>Enter the number of {unitWord} to see the cost.</span>
        ) : pricing ? (
          <span style={{ color: V.faint }}>Pricing this branch…</span>
        ) : quote?.ok && quote.manual ? (
          /* Console-created accounts are invoiced off-platform — don't show a
             Ksh 0 price table that implies the branch is free. */
          <span style={{ color: V.muted }}>{quote.message}</span>
        ) : quote?.ok ? (
          <>
            <PriceRow label="Due now (prorated)" value={`Ksh ${(quote.amountKES ?? 0).toLocaleString()}`} strong />
            <PriceRow
              label={`Then per cycle${quote.daysRemaining != null ? ` · aligns over ${quote.daysRemaining} day(s)` : ''}`}
              value={`Ksh ${(quote.recurringKES ?? 0).toLocaleString()}`}
            />
            <div style={{ fontSize: 12, color: V.faint, marginTop: 8 }}>
              Billed to {motherName}'s card and renewing with the rest of the group.
            </div>
          </>
        ) : (
          <span style={{ color: '#b45309' }}>{quote?.message || 'Could not price this branch right now.'}</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
        <GhostButton onClick={onCancel} disabled={creating}>Cancel</GhostButton>
        <PrimaryButton onClick={handleCreate} disabled={creating || !name.trim() || size <= 0}>
          {creating ? 'Creating…' : 'Create branch'}
        </PrimaryButton>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${V.line}`,
  fontSize: 13.5, fontFamily: 'inherit', color: V.ink, boxSizing: 'border-box', outline: 'none',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: V.muted }}>{label}</span>
      {children}
    </label>
  );
}

function PriceRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
      <span style={{ color: V.muted, fontSize: 12.5 }}>{label}</span>
      <span style={{ fontWeight: strong ? 800 : 700, fontSize: strong ? 15 : 13 }}>{value}</span>
    </div>
  );
}
